/** Gap between the header card and the drawer panel, and between the panel and the viewport bottom */
const GAP_TOP = 8;
const GAP_BOTTOM = 12;

/**
 * Supports the mobile menu drawer (snippets/header-drawer-ttt.liquid), whose
 * opening, closing, submenus and focus are handled by `header-drawer`:
 *
 * - Places the panel right below the header card and down to the bottom of the
 *   viewport, wherever the header is on screen (announcement bar, sticky header,
 *   scroll position), by setting `--header-drawer-ttt-top` and
 *   `--header-drawer-ttt-height` on itself.
 * - Keeps the close button working: see #onClick.
 */
class HeaderDrawerOffset extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // `toggle` doesn't bubble, so listen in the capture phase to catch the drawer's details
    this.addEventListener('toggle', this.#update, { capture: true, signal });
    // On window, capture phase: runs before the `on:click` handlers, which Component
    // dispatches from a capturing listener on document (see #onClick)
    window.addEventListener('click', this.#onClick, { capture: true, signal });
    window.addEventListener('resize', this.#update, { signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * When the drawer is open, the toggle button closes it through `header-drawer`, which
   * removes the details' `open` attribute right away because the panel has no closing
   * animation to wait for. The summary's default action then runs and toggles the details
   * open again, leaving the close icon visible and the panel hidden. Prevent that default
   * action only when closing; opening still relies on it. This must run before
   * `header-drawer` closes the drawer, while the details is still open.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const summary = event.target.closest('summary');
    const details = summary?.parentElement;

    if (details instanceof HTMLDetailsElement && details.matches('.header-drawer-ttt__container') && details.open) {
      event.preventDefault();
    }
  };

  #update = () => {
    const card = this.closest('.header__columns') ?? this.closest('.header__row');
    if (!(card instanceof HTMLElement)) return;

    const top = Math.max(card.getBoundingClientRect().bottom, 0) + GAP_TOP;
    const height = Math.max(window.innerHeight - top - GAP_BOTTOM, 0);

    // The header uses `contain: layout`, which makes it the containing block of the
    // fixed panel: offset the panel from the header's top instead of the viewport's.
    const header = this.closest('.header');
    const contained = header instanceof HTMLElement && /layout|paint|strict|content/.test(getComputedStyle(header).contain);
    const origin = contained ? header.getBoundingClientRect().top : 0;

    this.style.setProperty('--header-drawer-ttt-top', `${Math.round(top - origin)}px`);
    this.style.setProperty('--header-drawer-ttt-height', `${Math.round(height)}px`);
  };
}

if (!customElements.get('header-drawer-offset-ttt')) {
  customElements.define('header-drawer-offset-ttt', HeaderDrawerOffset);
}
